import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, FileText, Copy, Link2, Power, Printer, Check } from "lucide-react";
import { PageHeader, Panel, Badge } from "@/components/admin/ui";

function QRCard({ q, canManage, onChange }) {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(q.destination_url);
  const base = api.defaults.baseURL;

  const patch = async (body, msg) => {
    try { await api.patch(`/qr/${q.id}`, body); toast.success(msg); setEditing(false); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copy = async () => { await navigator.clipboard.writeText(q.destination_url); toast.success("Link copied"); };

  return (
    <Panel className={`p-5 flex flex-col ${q.enabled ? "" : "opacity-60"}`} data-testid={`qr-card-${q.type}`}>
      <div className="flex items-center justify-between mb-3">
        <div><div className="font-display font-semibold">{q.label}</div><div className="text-[11px] text-neutral-500">{q.store_name}</div></div>
        <Badge tone={q.enabled ? "green" : "red"} data-testid={`qr-enabled-${q.type}`}>{q.enabled ? "enabled" : "disabled"}</Badge>
      </div>
      <div className="bg-neutral-50 rounded-xl p-3 flex items-center justify-center mb-3">
        <img src={`${base}/qr/${q.id}/png`} alt={q.label} className="w-40 h-40" data-testid={`qr-img-${q.type}`}/>
      </div>
      <div className="text-[11px] text-neutral-500 break-all mb-3 flex-1">
        {editing ? (
          <div className="flex gap-1"><Input value={url} onChange={(e) => setUrl(e.target.value)} className="h-8 text-xs" data-testid={`qr-url-input-${q.type}`}/>
            <Button size="sm" onClick={() => patch({ destination_url: url }, "Destination updated")} data-testid={`qr-url-save-${q.type}`}><Check className="w-3 h-3"/></Button></div>
        ) : <span data-testid={`qr-dest-${q.type}`}>→ {q.destination_url}</span>}
        <div className="mt-1 text-neutral-400">{q.scans || 0} scans</div>
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-xs">
        <a href={`${base}/qr/${q.id}/png`} download target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full" data-testid={`qr-png-${q.type}`}><Download className="w-3 h-3 mr-1"/>PNG</Button></a>
        <a href={`${base}/qr/${q.id}/pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full" data-testid={`qr-pdf-${q.type}`}><FileText className="w-3 h-3 mr-1"/>PDF Sign</Button></a>
        <a href={`${base}/qr/${q.id}/pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full"><Printer className="w-3 h-3 mr-1"/>Print</Button></a>
        <Button size="sm" variant="outline" onClick={copy} data-testid={`qr-copy-${q.type}`}><Copy className="w-3 h-3 mr-1"/>Copy link</Button>
        {canManage && <>
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)} data-testid={`qr-replace-${q.type}`}><Link2 className="w-3 h-3 mr-1"/>Replace link</Button>
          <Button size="sm" variant="outline" onClick={() => patch({ enabled: !q.enabled }, q.enabled ? "QR disabled" : "QR enabled")} data-testid={`qr-toggle-${q.type}`}><Power className="w-3 h-3 mr-1"/>{q.enabled ? "Disable" : "Enable"}</Button>
        </>}
      </div>
    </Panel>
  );
}

export default function StoreQR() {
  const { sid } = useParams();
  const { user } = useAuth();
  const canManage = ["super_admin", "admin"].includes(user?.role) || user?.permissions?.qr === "manage";
  const [list, setList] = useState([]);
  const load = useCallback(() => { api.get(`/stores/${sid}/qr`).then((r) => setList(r.data)).catch(() => setList([])); }, [sid]);
  useEffect(load, [load]);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader eyebrow="Store · Marketing" title="QR Codes" testId="qr-title" subtitle="Printed codes always point to a redirect link, so you can replace the destination without reprinting."/>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((q) => <QRCard key={q.id} q={q} canManage={canManage} onChange={load}/>)}
      </div>
    </div>
  );
}
